import { useEffect, useState } from "react";
import { Smartphone, Monitor, Download, CheckCircle2, X, Share as ShareIcon, Plus } from "lucide-react";
import { useT } from "@/lib/i18n";

// PWA install section for the home page.
//   • Chrome / Edge / Android → uses the native `beforeinstallprompt` event.
//   • iOS Safari → shows a bilingual step-by-step modal (Share → Add to
//     Home Screen), because iOS does not expose a programmatic install API.
//   • Already installed → both cards flip to a "Installed ✓" success state.
const isIOSDevice = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

const isRunningStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true
  );
};

export default function PwaInstall() {
  const t = useT();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isRunningStandalone());
  const [showIOSModal, setShowIOSModal] = useState(false);
  const isIOS = isIOSDevice();

  useEffect(() => {
    const beforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", beforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", beforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const triggerInstall = async () => {
    if (isIOS && !deferredPrompt) {
      setShowIOSModal(true);
      return;
    }
    if (!deferredPrompt) {
      // No native prompt available (Firefox / older browsers / already-installed
      // Chrome). Fall back to instructions per platform.
      setShowIOSModal(true);
      return;
    }
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice?.outcome === "accepted") setInstalled(true);
    setDeferredPrompt(null);
  };

  const canNativeInstall = !!deferredPrompt;

  return (
    <section className="bg-white border-t border-[#E5E5E5]" data-testid="home-pwa-install">
      <div className="rn3-container py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="rn3-eyebrow text-[#8B1C22]">{t("home.install.eyebrow") || "Install the App"}</div>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-3 text-[#0A0A0A]">
            {t("home.install.h2_a") || "Roman Numerals III,"}{" "}
            <span className="italic text-[#C4A252]">{t("home.install.h2_b") || "always one tap away."}</span>
          </h2>
          <p className="mt-5 text-[#737373] leading-relaxed">
            {t("home.install.sub") || "Install our app to your phone or desktop for instant access to your portal, appointments, documents, and Adelis AI — no app store needed."}
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* MOBILE CARD */}
          <div className="border border-[#E5E5E5] bg-[#FAF7F0] p-8 flex flex-col" data-testid="pwa-card-mobile">
            <div className="w-14 h-14 bg-[#8B1C22] text-white flex items-center justify-center">
              <Smartphone className="w-7 h-7" strokeWidth={1.4} />
            </div>
            <div className="rn3-eyebrow mt-6 text-[#8B1C22]">{t("home.install.mobile.eyebrow") || "iPhone · Android"}</div>
            <h3 className="font-serif-display text-2xl mt-2 text-[#0A0A0A]">{t("home.install.mobile.title") || "Install on Mobile"}</h3>
            <p className="mt-3 text-sm text-[#737373] flex-1">
              {t("home.install.mobile.desc") || "Add Roman Numerals III to your Home Screen for a full-screen, native-app experience with push-style branded splash on launch."}
            </p>
            <div className="mt-6">
              {installed ? (
                <div className="inline-flex items-center gap-2 text-[#0A6B3A] text-sm font-medium" data-testid="pwa-mobile-installed">
                  <CheckCircle2 className="w-4 h-4" /> {t("home.install.installed") || "Installed on this device"}
                </div>
              ) : (
                <button
                  onClick={triggerInstall}
                  className="rn3-btn-primary"
                  data-testid="pwa-install-mobile"
                >
                  <Download className="w-4 h-4" /> {t("home.install.mobile.cta") || "Install on Mobile"}
                </button>
              )}
            </div>
          </div>

          {/* DESKTOP CARD */}
          <div className="border border-[#E5E5E5] bg-[#0A0A0A] text-white p-8 flex flex-col" data-testid="pwa-card-desktop">
            <div className="w-14 h-14 bg-[#C4A252] text-[#0A0A0A] flex items-center justify-center">
              <Monitor className="w-7 h-7" strokeWidth={1.4} />
            </div>
            <div className="rn3-eyebrow mt-6 text-[#C4A252]">{t("home.install.desktop.eyebrow") || "Mac · Windows · Chromebook"}</div>
            <h3 className="font-serif-display text-2xl mt-2">{t("home.install.desktop.title") || "Install on Desktop"}</h3>
            <p className="mt-3 text-sm text-[#bdbdbd] flex-1">
              {t("home.install.desktop.desc") || "Pin Roman Numerals III to your dock or taskbar. Works in Chrome, Edge, Brave, and Opera — opens like a native desktop app."}
            </p>
            <div className="mt-6">
              {installed ? (
                <div className="inline-flex items-center gap-2 text-[#C4A252] text-sm font-medium" data-testid="pwa-desktop-installed">
                  <CheckCircle2 className="w-4 h-4" /> {t("home.install.installed") || "Installed on this device"}
                </div>
              ) : (
                <button
                  onClick={triggerInstall}
                  className="rn3-btn-gold !text-white border-[#C4A252]"
                  data-testid="pwa-install-desktop"
                >
                  <Download className="w-4 h-4" />
                  {canNativeInstall
                    ? (t("home.install.desktop.cta") || "Install on Desktop")
                    : (t("home.install.desktop.cta_manual") || "How to install")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* iOS / manual install instructions modal */}
      {showIOSModal && (
        <div
          className="fixed inset-0 z-[9998] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setShowIOSModal(false)}
          data-testid="pwa-ios-modal"
        >
          <div
            className="bg-white max-w-md w-full p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowIOSModal(false)}
              className="absolute top-4 right-4 text-[#737373] hover:text-[#0A0A0A]"
              data-testid="pwa-ios-modal-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="rn3-eyebrow text-[#8B1C22]">{t("home.install.ios.eyebrow") || "How to install"}</div>
            <h3 className="font-serif-display text-2xl mt-2 text-[#0A0A0A]">
              {isIOS
                ? (t("home.install.ios.title_ios") || "Add to Home Screen on iPhone")
                : (t("home.install.ios.title_desktop") || "Install on your browser")}
            </h3>

            {isIOS ? (
              <ol className="mt-6 space-y-4 text-sm text-[#0A0A0A]">
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-[#8B1C22] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                  <span className="flex-1">
                    {t("home.install.ios.step1") || "Open this site in Safari (not Chrome or in-app browsers)."}
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-[#8B1C22] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                  <span className="flex-1 flex items-center flex-wrap gap-1">
                    {t("home.install.ios.step2a") || "Tap the"}{" "}
                    <ShareIcon className="w-4 h-4 inline-block text-[#8B1C22]" />
                    <b className="mx-1">{t("home.install.ios.share") || "Share"}</b>
                    {t("home.install.ios.step2b") || "icon at the bottom of Safari."}
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-[#8B1C22] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                  <span className="flex-1 flex items-center flex-wrap gap-1">
                    {t("home.install.ios.step3a") || "Scroll and tap"}{" "}
                    <Plus className="w-4 h-4 inline-block text-[#8B1C22]" />
                    <b className="mx-1">{t("home.install.ios.add_to_home") || "Add to Home Screen"}</b>
                    {t("home.install.ios.step3b") || ", then Add."}
                  </span>
                </li>
              </ol>
            ) : (
              <ol className="mt-6 space-y-4 text-sm text-[#0A0A0A]">
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-[#8B1C22] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                  <span className="flex-1">
                    {t("home.install.desktop.step1") || "Open romannumeralsiii.com in Chrome, Edge, Brave, or Opera."}
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-[#8B1C22] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                  <span className="flex-1">
                    {t("home.install.desktop.step2") || "Click the install icon on the right side of the address bar (looks like a screen with a down arrow)."}
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-[#8B1C22] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                  <span className="flex-1">
                    {t("home.install.desktop.step3") || "Click Install. The app will open in its own window and appear in your dock, taskbar, or apps folder."}
                  </span>
                </li>
              </ol>
            )}
            <button
              onClick={() => setShowIOSModal(false)}
              className="rn3-btn-primary mt-8 w-full justify-center"
              data-testid="pwa-ios-modal-got-it"
            >
              {t("home.install.ios.got_it") || "Got it"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
